import React from 'react';
import { ReactComponent as StepCompleteIcon } from '../assets/step-complete.svg';
import s from './DietPlanBuilder.module.css';
import { fmtInt } from '../lib/format';
import type { Plan } from '../types';

export type BuildStep = 'goals' | 'targets' | 'meals';

const STEPS: { key: BuildStep; n: number; label: string }[] = [
  { key: 'goals', n: 1, label: 'Goals' },
  { key: 'targets', n: 2, label: 'Meal targets' },
  { key: 'meals', n: 3, label: 'Meals' },
];

const STEP_INDEX: Record<BuildStep, number> = { goals: 0, targets: 1, meals: 2 };

export function BuilderTopBar({
  mode,
  activeStep,
  plan,
  onStep,
  onAssessment,
  onClose,
  mealsReviewCount,
  flowPastMeals,
}: {
  mode: 'choose' | 'build';
  activeStep: BuildStep | null;
  plan: Plan | null;
  onStep: (step: BuildStep) => void;
  onAssessment: () => void;
  onClose: () => void;
  mealsReviewCount?: number;
  flowPastMeals?: boolean;
}) {
  const sub = (key: BuildStep) => {
    if (!plan) return '';
    if (key === 'goals') return `${fmtInt(plan.kcal)} kcal`;
    if (key === 'targets') return `${plan.meals.filter(m => !m.skip).length} meals`;
    if (key === 'meals') {
      if (mealsReviewCount && mealsReviewCount > 0) return `${mealsReviewCount} to review`;
      return `${plan.meals.filter(m => !m.skip).length} meals`;
    }
    return plan.name;
  };

  return (
    <div className={s.topBar}>
      <span className={s.topBarBrand}>Create Diet Plan</span>
      <div className={s.topBarSteps}>
        {mode === 'build' &&
          STEPS.map(st => {
            const cur = !flowPastMeals && activeStep === st.key;
            const activeIdx = activeStep != null ? STEP_INDEX[activeStep] : flowPastMeals ? 3 : -1;
            const done = activeIdx > STEP_INDEX[st.key];
            const on = cur || done;
            return (
              <button
                type="button"
                key={st.key}
                className={s.stepNav}
                data-active={cur}
                onClick={() => onStep(st.key)}
                disabled={!plan}
              >
                <span className={s.stepCircle} data-cur={cur} data-done={done && !cur}>
                  {done && !cur ? (
                    <StepCompleteIcon className={s.stepCompleteIcon} />
                  ) : (
                    st.n
                  )}
                </span>
                <span className={s.stepLabels}>
                  <span className={s.stepLabel} data-on={on}>{st.label}</span>
                  <span className={s.stepSub}>{sub(st.key)}</span>
                </span>
              </button>
            );
          })}
      </div>
      {mode === 'build' && (
        <button type="button" className={s.btnOutlineGreen} onClick={onAssessment}>View assessment</button>
      )}
      <button type="button" className={s.topBarClose} onClick={onClose} aria-label="Close">×</button>
    </div>
  );
}

export function MobileNav({
  title,
  sub,
  step,
  steps = 3,
  onBack,
}: {
  title: string;
  sub?: string;
  step: number;
  steps?: number;
  onBack: () => void;
}) {
  return (
    <div className={s.header}>
      <button type="button" className={s.back} onClick={onBack} aria-label="Back">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <div className={s.grow}>
        <div className={s.title}>{title}</div>
        {sub && <div className={s.sub}>{sub}</div>}
      </div>
      {step >= 0 && (
        <div className={s.steps} aria-hidden>
          {Array.from({ length: steps }, (_, i) => (
            <div key={i} className={s.stepDot} data-on={i <= step} data-cur={i === step} />
          ))}
        </div>
      )}
    </div>
  );
}

export function PageHead({ title, sub, action }: { title: string; sub?: string; action?: React.ReactNode }) {
  return (
    <div className={s.pageHead}>
      <div>
        <h3 className={s.pageTitle}>{title}</h3>
        {sub && <p className={s.pageSub}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}

export function ShellFooter({
  statusText,
  statusTone = 'ok',
  showJump,
  onJump,
  canBack,
  onBack,
  onDraft,
  draftBusy,
  primaryLabel,
  onPrimary,
  primaryDisabled,
}: {
  statusText?: string;
  statusTone?: 'ok' | 'warn' | 'muted';
  showJump?: boolean;
  onJump?: () => void;
  canBack?: boolean;
  onBack?: () => void;
  onDraft?: () => void;
  draftBusy?: boolean;
  primaryLabel?: string;
  onPrimary?: () => void;
  primaryDisabled?: boolean;
}) {
  return (
    <div className={s.shellFooter}>
      <div className={s.shellFooterStatus}>
        {statusText && (
          <>
            <span className={s.statusDot} data-tone={statusTone} />
            <span className={s.statusText} data-tone={statusTone}>{statusText}</span>
          </>
        )}
        {showJump && onJump && (
          <button type="button" className={s.btnLink} onClick={onJump}>Show next →</button>
        )}
      </div>
      <div className={s.shellFooterActions}>
        {canBack && onBack && (
          <button type="button" className={s.btnOutlineGray} onClick={onBack}>Back</button>
        )}
        {onDraft && (
          <button type="button" className={s.btnOutlineGreen} onClick={onDraft} disabled={draftBusy}>
            {draftBusy ? 'Saving…' : 'Save as draft'}
          </button>
        )}
        {primaryLabel && onPrimary && (
          <button type="button" className={s.btnSolidGreen} onClick={onPrimary} disabled={primaryDisabled}>
            {primaryLabel}
          </button>
        )}
      </div>
    </div>
  );
}
